import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CourseSummaryComponent } from "../../../components/course/view"
import { useSelector } from "react-redux";
import { LearningRecordCourse } from "src/types/coaching/learners";
import React, { useEffect, useMemo } from "react";
import { CourseContext, CourseContextProps } from "../../../components/course/view/context";

import _ from 'lodash';
import cx from 'classnames';

import { useDispatch } from "react-redux";

import { CourseStructureComponent } from "../../../components/course/view/structure";
import { Assessment, Course } from "src/types/coaching/courses";
import { editCourse, fetchDraftCourse, fetchLessonForCourse, fetchLessonPreviewForCourse } from "src/redux/actions/coaching/courses";

import styles from "./preview.module.scss";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { CourseNotFound } from "src/pages/coaching/components/notFound";

interface PreviewCourseState {
  course: LearningRecordCourse | null;
}

export const PreviewCoursePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const [previewCourse, setPreviewCourse] = React.useState<PreviewCourseState>({
    course: null,
  });

  const courseId = params.id;

  const course = useSelector((state: any) =>
    _.get(state.courses, `course[${courseId}].item`) as Course,
  );

  const loading = useSelector((state: any) =>
    _.get(state.courses, `course[${courseId}].loading`),
  );

  const courseErrorCode = useSelector((state: any) => {
    return _.get(state.courses, `course[${courseId}].error.errors[0].code`);
  });

  const [courseContext, setCourseContext] = React.useState<CourseContextProps>({
    id: courseId,
    startAssessment: () => { },
    endAssessment: () => { },
  });

  useEffect(() => {
    if (courseId && !course) {
      dispatch(fetchDraftCourse(courseId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: courseId,
        onBack: handleBackClicked,
        onContentClicked: handleContentClicked,
        startAssessment: () => { },
        endAssessment: () => { },
      }
    });
  }, []);

  useEffect(() => {
    if (!course) {
      return;
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        startCourse: handleStartCourse,
      }
    });

    setPreviewCourse((prev) => {
      return {
        ...prev,
        course: {
          id: course.id!,
          course: course,
          sessions: [],
          status: "NOT_STARTED",
        },
      }
    });
  }, [course]);

  const handleBackClicked = () => {
    navigate(`/coaching/manage`);
  }

  const handleContentClicked = (index) => {
    navigate(`/coaching/manage/courses/preview/${courseId}/contents?index=${index}`);
  }

  const handleStartCourse = () => {
    handleContentClicked(0);

    setPreviewCourse((prev) => {
      return {
        ...prev,
        course: {
          ...prev.course!,
          status: "IN_PROGRESS",
        }
      }
    });
  }

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (courseErrorCode === 10007) {
    return <CourseNotFound onBack={() => {
      navigate(`/coaching/manage`);
    }} />
  }

  if ((loading && previewCourse.course?.id != courseId) || !previewCourse.course) {
    return <div className={styles.loading}>Loading course...</div>
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <CourseSummaryComponent
        course={previewCourse.course!}
      />
    </CourseContext.Provider>
  );
}

export const PreviewCourseStructurePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [previewCourse, setPreviewCourse] = React.useState<PreviewCourseState>({
    course: null,
  });

  const courseId = params.id;

  const course = useSelector((state: any) =>
    _.get(state.courses, `course[${courseId}].item`) as Course,
  );

  const loading = useSelector((state: any) =>
    _.get(state.courses, `course[${courseId}].loading`),
  );
  const contentIndex = useMemo(() => {
    return parseInt(searchParams.get("index") || "0");
  }, [searchParams]);

  const childContentIndex = useMemo(() => {
    return parseInt(searchParams.get("child") || "0");
  }, [searchParams]);

  const courseContent = useMemo(() => {
    const content = _.get(course, `contents[${contentIndex}]`);
    if (childContentIndex >= 0 && content?.entityType === 'section') {
      return _.get(content, `contents[${childContentIndex}]`);
    }
    return content;
  }, [course, contentIndex, childContentIndex]);

  const courseContentSession = useMemo(() => {
    const sessions = previewCourse.course?.sessions || [];
    const item = sessions.find((item) => item.id === courseContent?.id && item.type === courseContent?.entityType);
    return item;
  }, [previewCourse, contentIndex]);

  const contents = useMemo(() => course?.contents || [], [course]);

  const asset = useSelector((state: any) => {
    if (courseContent?.entityType !== "lesson") {
      return undefined;
    }

    const assetLoading = _.get(state, `courses.edit.assets['${courseContent.asset.id}'].loading`);
    const item = _.get(state, `courses.edit.assets['${courseContent.asset.id}'].item`);

    const previewItem = _.get(state, `courses.edit.assets['${courseContent.asset.id}'].preview.item`);
    const previewLoading = _.get(state, `courses.edit.assets['${courseContent.asset.id}'].preview.loading`);

    return {
      loading: assetLoading,
      item: item,
      preview: previewItem,
    };
  }, _.isEqual);


  const [courseContext, setCourseContext] = React.useState<CourseContextProps>({
    id: courseId,
    startAssessment: () => { },
    endAssessment: () => { },
  });

  useEffect(() => {
    if (courseId && !course) {
      dispatch(fetchDraftCourse(courseId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: courseId,
        onBack: handleBackClicked,
        onContentClicked: handleContentClicked,
        startAssessment: () => { },
        endAssessment: () => { },
      }
    });
  }, []);

  useEffect(() => {
    if (!course) {
      return;
    }

    dispatch(editCourse(course));

    setPreviewCourse((prev) => {
      return {
        ...prev,
        course: {
          id: course.id!,
          course: course,
          sessions: [],
          status: "IN_PROGRESS",
        },
      }
    });
  }, [course]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContent: courseContent,
        createCourseContentSession: handleCreateCourseContentSession,
        fetchCourseContentSession: handleFetchCourseContentSession,
        updateCourseContentSession: handleUpdateCourseContentSession,
        completeCourseContentSession: handleCompleteCourseContentSession,
        recordAnswerForQuestion: handleRecordAnswerForQuestion,
        recordQuestionSession: handleRecordQuestionSession,
        fetchAssessmentReport: handleFetchAssessmentReport,
        createLessonAnalyticsEvent: handleCreateLessonAnalyticsEvent,
        recordLessonAnalyticsEvent: handleRecordLessonAnalyticsEvent,
        endAssessment: handleEndAssessment,
        fetchLesson: handleFetchLesson,
        fetchLessonPreview: handleFetchLessonPreview,
      }
    });
  }, [courseContent]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        asset: asset,
      }
    });
  }, [asset]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContentSession: courseContentSession,
      }
    });
  }, [courseContentSession]);

  //Assessment related side effects
  const handleBackClicked = () => {
    navigate(`/coaching/manage/courses/preview/${courseId}`);
  }

  const handleContentClicked = (index: number, section?: number | null) => {
    if (section != null && section !== -1) {
      navigate(`/coaching/manage/courses/preview/${courseId}/contents?index=${index}&child=${section}`);
    } else {
      navigate(`/coaching/manage/courses/preview/${courseId}/contents?index=${index}`);
    }
  }

  const handleCreateCourseContentSession = (courseContent) => {
    setPreviewCourse((prev) => {
      let sessions = prev.course?.sessions || [];
      const session = sessions.find((session) => session.id === courseContent.id);
      if (session) {
        return prev;
      }

      sessions = [
        ...sessions,
        {
          id: courseContent.id,
          type: courseContent.entityType,
          session: courseContent.id,
          percentCompleted: 0,
          duration: 0,
        }
      ];

      return {
        ...prev,
        course: {
          ...prev.course!,
          sessions: sessions
        }
      }
    });

    if (courseContent.entityType === "assessment") {
      setCourseContext((prev) => {
        return {
          ...prev,
          activeAssessment: courseContent as Assessment,
        }
      });
    }
  }

  const handleFetchCourseContentSession = (courseContent, session) => {
    handleCreateCourseContentSession(courseContent);
  }

  const handleUpdateCourseContentSession = (courseContent, session, duration, progress) => {
    setPreviewCourse((prev) => {
      const sessions = (prev.course?.sessions || []).map((item) => {
        if (item.id === courseContent.id) {
          return {
            ...item,
            duration: duration,
            percentCompleted: progress,
          }
        }

        return item;
      });

      return {
        ...prev,
        course: {
          ...prev.course!,
          sessions: sessions,
        }
      }
    });
  }

  const handleCompleteCourseContentSession = (courseContent, session, duration, progress) => {
    setPreviewCourse((prev) => {
      const sessions = (prev.course?.sessions || []).map((item) => {
        if (item.id === courseContent.id) {
          return {
            ...item,
            duration: duration,
            percentCompleted: progress,
            status: "COMPLETED",
          }
        }

        return item;
      });

      return {
        ...prev,
        course: {
          ...prev.course!,
          sessions: sessions,
        }
      }
    });

    if (courseContent.entityType === "assessment") {
      handleEndAssessment(courseContent as Assessment);
    }
  }

  const handleEndAssessment = (assessment) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        activeAssessment: null,
      }
    });
  }

  const handleRecordAnswerForQuestion = (assessment, question, answer) => {
    setPreviewCourse((prev) => {
      const sessions = (prev.course?.sessions || []).map((session) => {
        if (session.id === assessment.id) {
          let questions = session.questions || [];
          const questionIndex = questions.findIndex((q) => q.id === question);
          if (questionIndex === -1) {
            questions = [
              ...questions,
              {
                id: question,
                question: question,
                answer: answer,
                duration: 0,
              }
            ];
          } else {
            questions = questions.map((q) => {
              if (q.id === question) {
                return {
                  ...q,
                  answer: answer,
                }
              }

              return q;
            });
          }

          return {
            ...session,
            questions: questions,
            percentCompleted: _.size(questions) / (_.size(assessment.questions) || 1),
          }
        }

        return session;
      });

      return {
        ...prev,
        course: {
          ...prev.course!,
          sessions: sessions,
        }
      }
    });
  }

  const handleRecordQuestionSession = (assessment, question, answer, duration) => {

  }

  const handleFetchAssessmentReport = (courseContent, session) => {
    setPreviewCourse((prev) => {
      const sessions = (prev.course?.sessions || []).map((item) => {
        if (item.id === courseContent.id) {

          const questions = (item.questions || []).map(q => {
            const question = courseContent.questions.find((question) => question.id === q.id);
            const correct = q.answer === question?.answer;
            const score = correct ? 1 : 0;

            return {
              ...q,
              id: q.id!,
              correctAnswer: question?.answer,
              correct: q.answer === question?.answer,
              feedback: "",
              score: score,
              duration: 0,
            }
          });

          const report = {
            id: courseContent.id,
            session: session,
            questions: questions,
            percentCompleted: 0,
            duration: 0,
            score: 0,
            startDate: new Date(),
            endDate: new Date(),
          }

          return {
            ...item,
            report: report,
            status: "COMPLETED",
          }
        }

        return item;
      });

      return {
        ...prev,
        course: {
          ...prev.course!,
          sessions: sessions,
        }
      }
    });

    setCourseContext((prev) => {
      return {
        ...prev,
        activeAssessment: courseContent as Assessment,
      }
    });
  }

  //Lesson
  const handleCreateLessonAnalyticsEvent = (lesson) => {

  }

  const handleRecordLessonAnalyticsEvent = (lesson, session, page) => {

  }

  const handleFetchLesson = (lesson) => {
    dispatch(fetchLessonForCourse({
      course: course.id!,
      lesson: lesson.id,
      asset: lesson.asset.id,
    }));
  }

  const handleFetchLessonPreview = (lesson) => {
    dispatch(fetchLessonPreviewForCourse({
      course: course.id!,
      lesson: lesson.id,
      asset: lesson.asset.id,
      includeText: true,
    }));
  }

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if ((loading && previewCourse.course?.id != courseId) || !previewCourse.course) {
    return <div className={styles.loading}>Loading course...</div>
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <CourseStructureComponent course={previewCourse.course!} />
    </CourseContext.Provider>
  );
}